import Highcharts from "highcharts";
import { z } from "zod";

import {
  ALL_BANDS,
  bandDisplayName,
  bandFor,
  formatScore,
} from "../sentimentScoreBands";
import {
  hoverLabel,
  resolveAxisTitles,
  resolveFieldLabels,
  unitFrom,
} from "../data/payloadLabels";

// ---------------------------------------------------------------------------
// sentiment_histogram — vertical bars over duration buckets, colored by the
// average sentiment of the calls in each bucket.
//
// What the payload's fields are (verified against the response, not assumed):
//   x     bucket label the backend already formatted, e.g. "91-182". Consecutive
//         labels step by exactly metadata.bucket_seconds (91), so the numbers are
//         seconds — but the backend doesn't say seconds *of what*, so the x axis
//         stays untitled unless it sends x_axis_label.
//   y     calls in the bucket. Summing every y in the sample gives 1491, which
//         is exactly metadata.total_calls — that's why the y axis can be titled
//         "Calls" outright rather than left blank.
//   score average sentiment of the bucket, 0..1, and NULLABLE: an empty bucket
//         (y = 0) sends score: null. Nulls get the muted "No sentiment" band.
//
// Shape accepted (both, since the registry conversion path can hand over the
// bare array while the dashboard hands over the whole envelope):
//   { data: [{ x, y, score }], metadata: {...} }
//   [{ x, y, score }]
// ---------------------------------------------------------------------------

// Validated per row (below) rather than as an array, so one malformed bucket
// drops that bar instead of blanking the widget.
const HistogramBucketSchema = z.object({
  x: z.string(),
  y: z.number(),
  // Explicitly nullable — the empty bucket in the real payload sends null.
  score: z.number().nullable().optional(),
  // The backend's own wording for this bucket's sentiment ("Positive lean",
  // "Neutral / Mixed", …), as sent by the scatter endpoint. Used in the hover
  // and the legend when present so both charts speak one vocabulary.
  sentiment_label: z.string().nullable().optional(),
});

type HistogramBucket = z.infer<typeof HistogramBucketSchema>;

// Axis titles are resolved by resolveAxisTitles (it accepts both the x_label
// and x_axis_label spellings the backend uses), so they're deliberately not
// declared here.
const MetadataSchema = z.object({
  bucket_seconds: z.number().optional(),
  total_calls: z.number().optional(),
  // Calls the backend pulled into range because they exceeded the last bucket.
  // Reported as a bare number — it does NOT say which bucket absorbs them, so
  // the subtitle states the count without claiming where they landed.
  clamped_calls: z.number().optional(),
  score_scale: z.string().optional(),
});

type HistogramMetadata = z.infer<typeof MetadataSchema>;

// metadata stays `unknown` here and is validated separately below: zod strips
// keys a schema doesn't declare, which would swallow the axis-label keys before
// resolveAxisTitles sees them.
const EnvelopeSchema = z.object({
  data: z.array(z.unknown()).optional(),
  metadata: z.unknown().optional(),
});

const formatInt = (value: number): string => value.toLocaleString();

// "91-182" → "91–182": a real en dash, since the label is a numeric range and
// the hyphen reads as a minus sign at small sizes. Anything that isn't a plain
// "number-number" range is passed through untouched.
const prettifyRange = (label: string): string =>
  /^\s*-?\d+(\.\d+)?\s*-\s*-?\d+(\.\d+)?\s*$/.test(label)
    ? label.replace(/\s*-\s*/, "–")
    : label;

type ParsedPayload = {
  buckets: HistogramBucket[];
  metadata: HistogramMetadata;
  /** Untouched, so the label resolver can read keys this file doesn't declare. */
  rawMetadata: unknown;
};

const parsePayload = (data: unknown): ParsedPayload => {
  const envelope = Array.isArray(data) ? undefined : EnvelopeSchema.safeParse(data).data;
  const rawRows: unknown[] = Array.isArray(data) ? data : (envelope?.data ?? []);
  const rawMetadata = envelope?.metadata;

  const buckets = rawRows.reduce<HistogramBucket[]>((acc, row) => {
    const parsed = HistogramBucketSchema.safeParse(row);
    if (parsed.success) acc.push(parsed.data);
    return acc;
  }, []);

  return {
    buckets,
    metadata: MetadataSchema.safeParse(rawMetadata).data ?? {},
    rawMetadata,
  };
};

const buildSubtitle = (
  buckets: HistogramBucket[],
  metadata: HistogramMetadata,
): string => {
  const calls =
    metadata.total_calls ?? buckets.reduce((sum, b) => sum + b.y, 0);

  const parts = [`${formatInt(calls)} calls`];
  if (metadata.bucket_seconds) {
    parts.push(`${formatInt(metadata.bucket_seconds)}s buckets`);
  }

  const base = `<span style="color:#64748B;">${parts.join(" · ")}</span>`;
  if (!metadata.clamped_calls) return base;

  // Worth saying out loud: some calls were moved to fit the range, so the tail
  // buckets aren't a pure reading of their own label.
  return (
    `${base} <span style="color:#B45309;font-weight:600;">· ` +
    `${formatInt(metadata.clamped_calls)} clamped into range</span>`
  );
};

const buildSentimentHistogramOptions = (
  data: unknown,
  spec: unknown,
): Highcharts.Options => {
  const { buckets, metadata, rawMetadata } = parsePayload(data);

  const categories = buckets.map((b) => prettifyRange(b.x));
  const totalCalls =
    metadata.total_calls ?? buckets.reduce((sum, b) => sum + b.y, 0);

  // x falls back to nothing — the bucket labels are self-describing ranges and
  // we won't assert what they measure. y falls back to "Calls", which is
  // verified arithmetic rather than a guess (see the header).
  const { x: xTitle, y: yTitle } = resolveAxisTitles(rawMetadata, spec, {
    x: null,
    y: "Calls",
  });
  const labels = resolveFieldLabels(rawMetadata);

  // Short hover row labels — see the scatter renderer: field_labels can hold
  // explanatory prose, and a hover row needs a name. The bucket caption below
  // is the one place the full wording still fits.
  // "Sentiment" is fixed rather than taken from field_labels: the backend's
  // wording there is "Average sentiment", which wraps onto a second line in a
  // hover and adds nothing the score doesn't already say.
  const hoverLabels = {
    calls: hoverLabel([labels.y, yTitle], "Calls"),
    sentiment: "Sentiment",
  };
  // Unit for the bucket range in the hover heading, e.g. "sec" out of
  // "Call duration (sec), equal-width buckets".
  const bucketUnit = unitFrom(labels.x, xTitle);

  // Typed structurally, not as XAxisTitleOptions/YAxisTitleOptions: Highcharts
  // types the x-axis title's `text` as string|null but the y-axis's as
  // string|undefined, even though null is the documented way to remove either.
  const axisTitle = (
    text: string | null,
  ): { text: string | null; style?: Highcharts.CSSObject } =>
    text
      ? { text, style: { color: "#64748B", fontSize: "11px" } }
      : { text: null };

  // One series per band, all sharing the same category axis. grouping:false is
  // what keeps each bar centred in its own bucket instead of Highcharts slicing
  // the slot into five side-by-side columns — a bucket belongs to exactly one
  // band, so the "overlapping" series never actually collide. Doing it this way
  // buys a native legend with click-to-filter and needs no colorAxis module.
  const series: Highcharts.SeriesColumnOptions[] = ALL_BANDS.map((band) => {
    const bandBuckets = buckets.filter((b) => bandFor(b.score) === band);
    return {
      type: "column" as const,
      // Named the way the backend names it when it sends sentiment_label, so the
      // legend and hover share one vocabulary across both sentiment charts.
      name: bandDisplayName(band, bandBuckets),
      color: band.color,
      // A bucket that belongs to another band is null here, not 0 — 0 would draw
      // a baseline stub and, worse, report as a real value in the tooltip.
      data: buckets.map((b) =>
        bandFor(b.score) === band
          ? {
              y: b.y,
              // Band word omitted on purpose — it's in the legend and the bar
              // color; the hover shows the bucket, its volume and its score.
              custom: { bucket: prettifyRange(b.x), score: b.score },
            }
          : null,
      ),
    };
  }).filter((s) =>
    // Drop bands this payload has nothing in, so the legend only advertises
    // sentiment levels that are actually on screen.
    s.data?.some((point) => point !== null),
  );

  return {
    chart: {
      type: "column",
      animation: false,
      spacingBottom: 12,
    },
    title: { text: "" },
    subtitle: {
      text: buildSubtitle(buckets, metadata),
      align: "left",
      useHTML: true,
      style: { fontSize: "11px" },
    },
    credits: { enabled: false },
    accessibility: { enabled: false },
    xAxis: {
      categories,
      title: axisTitle(xTitle),
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
      labels: {
        style: { color: "#64748B", fontSize: "10px" },
        // Long "1729–1820" labels won't fit twenty-across on a narrow widget;
        // let Highcharts rotate them rather than drop every other one.
        autoRotation: [0, -45, -90],
      },
    },
    yAxis: {
      title: axisTitle(yTitle),
      min: 0,
      gridLineWidth: 1,
      gridLineColor: "#F1F5F9",
      labels: { style: { color: "#64748B", fontSize: "10px" } },
    },
    legend: {
      // Right side of the same top row the left-aligned subtitle sits on,
      // rather than "center" — a long subtitle (calls · buckets · clamped
      // count) would otherwise run into a centered legend.
      align: "right",
      verticalAlign: "top",
      symbolRadius: 3,
      itemStyle: { fontSize: "11px", fontWeight: "600", color: "#334155" },
      itemHoverStyle: { color: "#0F172A" },
    },
    tooltip: {
      useHTML: true,
      outside: true,
      borderWidth: 0,
      borderRadius: 10,
      backgroundColor: "#0F172A",
      shadow: false,
      padding: 0,
      formatter() {
        const ctx = this as unknown as {
          y?: number;
          point: {
            options: {
              custom?: { bucket?: string; score?: number | null };
            };
          };
        };
        const custom = ctx.point.options.custom ?? {};
        const calls = ctx.y ?? 0;
        const share =
          totalCalls > 0 ? `${((calls / totalCalls) * 100).toFixed(1)}%` : "—";

        // white-space:normal is load-bearing: Highcharts hard sets
        // white-space:nowrap on the wrapper of a useHTML tooltip
        // (HTMLElement#htmlCss), so without this a long label can't wrap and
        // prints outside the measured box.
        const row = (label: string, value: string): string =>
          `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:14px;margin-top:2px;white-space:normal;">
             <span style="opacity:0.65;">${label}</span>
             <span style="font-weight:700;white-space:nowrap;">${value}</span>
           </div>`;

        // Just the score, matching the scatter: the band word is already in the
        // bar's color and the legend.
        const sentimentValue =
          typeof custom.score === "number" ? formatScore(custom.score) : "No score";

        // The bucket range is the bar's identity, so it heads the card. Its unit
        // rides along on that same line ("0–91 sec") rather than as a caption:
        // the backend's x wording is a full sentence here ("Call duration (sec),
        // equal-width buckets") and would wrap to two lines above every bar.
        const bucketHeading = bucketUnit
          ? `${custom.bucket ?? ""} ${bucketUnit}`
          : (custom.bucket ?? "");

        return `
          <div style="padding:10px 12px;min-width:170px;max-width:240px;color:#f8fafc;font-size:12px;line-height:1.35;white-space:normal;">
            <div style="font-weight:700;font-size:13px;margin-bottom:4px;">${bucketHeading}</div>
            ${row(hoverLabels.calls, formatInt(calls))}
            ${row("Share", share)}
            <div style="margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,0.15);">
              ${row(hoverLabels.sentiment, sentimentValue)}
            </div>
          </div>
        `;
      },
    },
    plotOptions: {
      column: {
        grouping: false,
        borderWidth: 0,
        borderRadius: 2,
        pointPadding: 0.08,
        groupPadding: 0.08,
        states: { inactive: { opacity: 0.25 } },
        dataLabels: {
          enabled: true,
          crop: false,
          overflow: "allow" as const,
          style: {
            fontSize: "9px",
            fontWeight: "600",
            color: "#475569",
            textOutline: "none",
          },
          formatter() {
            const value = (this as unknown as { y?: number }).y ?? 0;
            // Empty buckets get no label — a floating "0" over a bar that isn't
            // there is noise, and the gap already reads as zero.
            return value > 0 ? formatInt(value) : "";
          },
        },
      },
    },
    // Same context-button placement as every other chart in the registry: the
    // hamburger sits in the widget header strip above the plot. ChartFactory
    // layers the menu items (exports + conversions) on top of this from
    // buildContextMenu, driven by the registry's exportItems.
    exporting: {
      enabled: true,
      fallbackToExportServer: true,
      buttons: {
        contextButton: {
          align: "right",
          verticalAlign: "top",
          y: -43,
          x: -20,
        },
      },
    },
    series,
  };
};

export function renderSentimentHistogram(
  data: unknown,
  spec: unknown,
): Highcharts.Options {
  return buildSentimentHistogramOptions(data, spec);
}
